import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfrontoAnni } from "@/components/accordi/ConfrontoAnni";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Trophy } from "lucide-react";

const COLS = ["accessi_landing", "accessi_area", "preventivi_generati", "preventivi_salvati", "richieste_consulenza", "documentazione_caricata", "proposte_emesse", "polizze_attivate", "referral_ricevuti"];
const SHORT = { accessi_landing: "Landing", accessi_area: "Area", preventivi_generati: "Prev. generati", preventivi_salvati: "Prev. salvati", richieste_consulenza: "Consulenze", documentazione_caricata: "Documenti", proposte_emesse: "Proposte", polizze_attivate: "Polizze", referral_ricevuti: "Referral" };
const sel = "h-10 rounded-md border bg-white px-2 text-sm";

// Classifica: polizze attivate, poi conversione preventivo→polizza, poi preventivi salvati.
const Classifica = ({ righe }) => {
  const top = righe.filter((r) => r.preventivi_salvati > 0 || r.polizze_attivate > 0)
    .sort((a, b) => b.polizze_attivate - a.polizze_attivate || b.conv_preventivo_polizza - a.conv_preventivo_polizza || b.preventivi_salvati - a.preventivi_salvati).slice(0, 5);
  return (
    <Card className="rounded-xl p-4" data-testid="report-classifica">
      <div className="mb-2 flex items-center gap-2 font-heading font-semibold text-forest"><Trophy className="h-4 w-4 text-terracotta" />Classifica accordi nel periodo</div>
      {top.length === 0 ? <p className="text-sm text-muted-foreground">Ancora nessun preventivo salvato nel periodo.</p> : (
        <ol className="space-y-1.5">
          {top.map((r, i) => (
            <li key={r.codice} className="flex items-center gap-3 text-sm" data-testid={`classifica-${i + 1}`}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-terracotta text-white" : "bg-sage text-forest"}`}>{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-forest">{r.denominazione} <span className="font-mono text-xs text-muted-foreground">{r.codice}</span></span>
              <span className="tabular-nums text-muted-foreground">{r.polizze_attivate} polizze · {r.conv_preventivo_polizza}% conv. · {r.preventivi_salvati} prev.</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

// Funnel e KPI per accordo con filtri ed export Excel/CSV.
export const ReportAccordi = ({ accordi, meta }) => {
  const [f, setF] = useState({ da: "", a: "", codice: "", product: "", stato: "", conversione: "" });
  const [data, setData] = useState(null);
  const qs = (extra = {}) => new URLSearchParams({ ...f, ...extra }).toString();
  const load = () => api.get(`/admin/accordi/report?${qs()}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [f]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const esporta = async (format) => {
    try {
      const { data: blob } = await api.get(`/admin/accordi/report?${qs({ format })}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `report-accordi.${format}`; a.click();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="report-accordi">
      <div className="flex flex-wrap items-end gap-2">
        <div><div className="text-xs text-muted-foreground">Dal</div><Input type="date" value={f.da} onChange={set("da")} data-testid="report-da" className="w-40" /></div>
        <div><div className="text-xs text-muted-foreground">Al</div><Input type="date" value={f.a} onChange={set("a")} data-testid="report-a" className="w-40" /></div>
        <select value={f.codice} onChange={set("codice")} data-testid="report-accordo" className={sel}><option value="">Tutti gli accordi</option>{accordi.map((c) => <option key={c.id} value={c.codice}>{c.codice} — {c.denominazione || c.cooperativa_nome}</option>)}</select>
        <select value={f.product} onChange={set("product")} data-testid="report-prodotto" className={sel}><option value="">Tutti i prodotti</option>{meta?.prodotti.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}</select>
        <select value={f.stato} onChange={set("stato")} data-testid="report-stato" className={sel}><option value="">Tutti gli stati</option>{meta?.stati.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}</select>
        <select value={f.conversione} onChange={set("conversione")} data-testid="report-conversione" className={sel}><option value="">Ogni conversione</option><option value="con_polizze">Con polizze attivate</option><option value="senza_polizze">Senza polizze</option></select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => esporta("xlsx")} data-testid="report-export-xlsx" className="gap-1.5"><FileSpreadsheet className="h-4 w-4" />Excel</Button>
          <Button variant="outline" onClick={() => esporta("csv")} data-testid="report-export-csv" className="gap-1.5"><Download className="h-4 w-4" />CSV</Button>
        </div>
      </div>
      {f.product && <p className="text-xs text-muted-foreground">Con un prodotto selezionato gli accessi (landing/area) non sono attribuibili e risultano 0.</p>}
      {data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="report-totali">
          {[["accessi_area", "Accessi area"], ["preventivi_salvati", "Preventivi salvati"], ["richieste_consulenza", "Consulenze"], ["polizze_attivate", "Polizze attivate"], ["conv_area_preventivo", "Conv. area→preventivo"]].map(([k, l]) => (
            <Card key={k} className="rounded-xl p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="font-heading text-2xl font-semibold text-forest" data-testid={`report-tot-${k}`}>{data.totali[k]}{k.startsWith("conv") ? "%" : ""}</div></Card>
          ))}
        </div>
      )}
      {data && <Classifica righe={data.righe} />}
      <ConfrontoAnni codice={f.codice} />
      <Card className="overflow-x-auto rounded-xl">
        <Table>
          <TableHeader><TableRow className="bg-sage/50"><TableHead>Accordo</TableHead>{COLS.map((c) => <TableHead key={c} className="text-right">{SHORT[c]}</TableHead>)}<TableHead className="text-right">% prev→polizza</TableHead></TableRow></TableHeader>
          <TableBody>
            {data?.righe.map((r) => (
              <TableRow key={r.codice} data-testid={`report-row-${r.codice}`}>
                <TableCell><div className="font-mono text-xs text-forest">{r.codice}</div><div className="text-xs text-muted-foreground">{r.denominazione} · {r.stato}</div></TableCell>
                {COLS.map((c) => <TableCell key={c} className="text-right tabular-nums">{r[c]}</TableCell>)}
                <TableCell className="text-right tabular-nums">{r.conv_preventivo_polizza}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
